import type { MultiSelectOption, MultiSelectValue } from '@/components/molecules';
import type { SelectedElementParams } from '@/components/molecules/inputs/MultiSelect/types';
import { useCallback, useMemo } from 'react';

import type { GetUIElementByType } from '@ballerine/common';

import type { TDynamicFormField } from '../../types';
import { MultiSelect } from '@/components/molecules';
import { useField } from '../../hooks/external';
import { useMountEvent } from '../../hooks/internal/useMountEvent';
import { useUnmountEvent } from '../../hooks/internal/useUnmountEvent';
import { FieldDescription } from '../../layouts/FieldDescription';
import { FieldErrors } from '../../layouts/FieldErrors';
import { FieldLayout } from '../../layouts/FieldLayout';
import { FieldPriorityReason } from '../../layouts/FieldPriorityReason';
import { useStack } from '../FieldList/providers/StackProvider';
import { MultiselectfieldSelectedItem } from './MultiselectFieldSelectedItem';

export type MultiselectFieldOption = {
  label: string;
  value: any;
};

export type IMultiselectFieldParams = {
  options: MultiselectFieldOption[];
  placeholder?: string;
};

export const MultiselectField: TDynamicFormField<GetUIElementByType<'multiselectfield'>> = ({
  element,
}) => {
  useMountEvent(element);
  useUnmountEvent(element);

  const { stack } = useStack();
  const { value, onChange, onBlur, onFocus, disabled } = useField<MultiSelectValue[] | undefined>(
    element,
    stack,
  );

  const multiselectOptions = useMemo(() => {
    return (
      element.params?.options?.map(option => ({ title: option.label, value: option.value })) || []
    );
  }, [element.params?.options]);

  const renderSelected = useCallback((params: SelectedElementParams, option: MultiSelectOption) => {
    return <MultiselectfieldSelectedItem option={option} params={params} />;
  }, []);

  const handleChange = useCallback(
    (value: MultiSelectValue[]) => {
      onChange(value.length ? value : undefined);
    },
    [onChange],
  );

  return (
    <FieldLayout element={element}>
      <MultiSelect
        value={value}
        disabled={disabled}
        searchPlaceholder={element.params?.placeholder}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={onFocus}
        options={multiselectOptions as MultiSelectOption[]}
        renderSelected={renderSelected}
      />
      <FieldDescription element={element} />
      <FieldPriorityReason element={element} />
      <FieldErrors element={element} />
    </FieldLayout>
  );
};
